///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Device-location camera modes behind the "my location" button, in the
/// style of the phone maps apps:
///
/// - follow: the camera keeps the latest geolocation fix
///   (dialog.userLocation, published by services/userLocationService.ts)
///   at screen center as it moves, at whatever altitude / tilt / bearing
///   the view has.
/// - heading: as follow, and the view also turns to the device compass
///   heading so "up" on screen is the way the phone points.
///
/// Entering a mode flies to the fix first (a TourPlayer leg: the usual
/// zoom-out-fly-zoom-in), to a neighbourhood altitude on the first visit
/// (widened when the fix is coarse, so an IP-derived desktop position is
/// not framed at street level) and at the current altitude on a
/// re-center. After landing the pose is tracked per frame from an eased
/// copy of the fix and heading, so a new GPS sample slides the view over
/// rather than jumping it.
///
/// Any gesture that moves the camera (motionSource "user") ends the mode;
/// the marker stays and the button re-centers on the next tap. Loss of the
/// fix (permission revoked, tracking stopped) ends it too.
///

import { type CameraParams, EarthCameraState } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import type { UserLocationFix } from "@/redux/dialogSlice"
import { trig } from "@stovokor/math/math"
import type { TourPlayer } from "./TourPlayer"

export type FollowMode = "follow" | "heading"

/**
 * Altitude (km) of the first flight to the fix: a local-area view, just
 * above the camera floor (Constants.cMinCameraAltitude, 10 km) so there
 * is still a step of zoom left under it
 */
const cFollowAltitudeKm = 12
/**
 * A re-center from above this altitude (km) flies in to cFollowAltitudeKm
 * instead of keeping the zoom: that high the dot is a speck on the globe
 * and "where am I" wants the local view back
 */
const cRecenterMaxAltitudeKm = 1500
/**
 * Coarse fixes widen the first view: altitude is at least this many times
 * the accuracy radius, so the whole uncertainty disc is in frame
 */
const cAccuracyAltitudeFactor = 4
/** Position easing time constant (s): a 1 Hz GPS stream reads as a glide */
const cPositionTau = 0.5
/** Heading easing time constant (s): quick enough to feel attached to the phone */
const cHeadingTau = 0.18
/** Minimum seconds between redux publishes of the tracked pose */
const cPublishIntervalSeconds = 0.1

function shortestTurn(from: number, to: number): number {
	return Math.atan2(Math.sin(to - from), Math.cos(to - from))
}

export class FollowBehavior {
	private context: EarthBrowserContext
	private cameraState: EarthCameraState
	private tourPlayer: TourPlayer

	private mode: FollowMode | null = null
	/** Waiting for a fix (and for any focus transition) before the entry flight */
	private pendingFlight = false
	/** A flight to the fix has happened since tracking began: re-centers keep the zoom */
	private hasFlown = false
	/** Eased copy of the fix (degrees) and compass heading (radians) */
	private easedLat = 0
	private easedLon = 0
	private easedHeading = 0
	private headingKnown = false
	private sincePublish = 0

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState, tourPlayer: TourPlayer) {
		this.context = context
		this.cameraState = cameraState
		this.tourPlayer = tourPlayer
	}

	public get active(): boolean {
		return this.mode !== null
	}

	public get currentMode(): FollowMode | null {
		return this.mode
	}

	/**
	 * Enter (or switch) mode. Switching follow -> heading on an active
	 * follow doesn't fly again: the per-frame tracking turns the view
	 */
	public start(mode: FollowMode): void {
		const wasActive = this.mode !== null
		this.mode = mode
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: mode })
		if (!wasActive) {
			this.pendingFlight = true
			this.sincePublish = 0
		}
	}

	/** Leave the mode where the camera is; the marker is not affected */
	public stop(): void {
		if (this.mode === null) return
		this.mode = null
		this.pendingFlight = false
		if (this.tourPlayer.playing) {
			this.tourPlayer.stop()
		} else {
			this.cameraState.saveCameraState()
		}
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "free" })
	}

	/** Per-frame tick (dt seconds); the caller ticks the TourPlayer for the entry flight */
	public update(dt: number): void {
		if (this.mode === null) return

		const location = engineBridge.getState()?.dialog.userLocation
		const fix = location?.fix ?? null
		if (!location || !fix) {
			// Still locating: wait. Tracking ended or failed: nothing to follow
			this.hasFlown = false
			if (location?.status !== "locating") this.stop()
			return
		}

		// Entering with an entity focused: the director released it; wait
		// for that transition to hand the camera back to Earth
		if (this.pendingFlight && this.context.focusManager.isTransitioning()) return

		// An entity took the camera (tap on a quake, a satellite...): the
		// focused-entity camera owns the pose now
		const focused = this.context.focusManager.getFocusedNode()
		if (focused && focused.id !== "earth") {
			this.stop()
			return
		}

		const headingRad = this.headingFor(location.headingDeg)

		if (this.pendingFlight) {
			this.pendingFlight = false
			this.easedLat = fix.lat
			this.easedLon = fix.lon
			if (headingRad !== null) {
				this.easedHeading = headingRad
				this.headingKnown = true
			}
			this.tourPlayer.startLeg(this.flightTarget(fix, headingRad))
			this.hasFlown = true
			return
		}

		// The entry flight owns the pose until it lands
		if (this.tourPlayer.playing) return

		// A gesture moved the camera since the last tracked pose: hand over
		if (this.cameraState.motionSource === "user") {
			this.stop()
			return
		}

		// Ease toward the latest fix and heading
		const kPos = 1 - Math.exp(-dt / cPositionTau)
		this.easedLat += (fix.lat - this.easedLat) * kPos
		this.easedLon += shortestTurn(this.easedLon * trig.rad, fix.lon * trig.rad) * trig.deg * kPos
		if (headingRad !== null) {
			if (!this.headingKnown) {
				this.easedHeading = headingRad
				this.headingKnown = true
			} else {
				const kHeading = 1 - Math.exp(-dt / cHeadingTau)
				this.easedHeading += shortestTurn(this.easedHeading, headingRad) * kHeading
			}
		}

		const current = this.cameraState.getCurrentParams()
		const tilt = Math.min(current.tilt, this.cameraState.maxTilt(current.radius) - 0.001)
		const bearing = this.mode === "heading" && this.headingKnown
			? this.cameraState.clipBearing(this.easedHeading)
			: current.bearing
		const target = EarthCameraState.paramsLookingAt(
			this.groundPoint(this.easedLat, this.easedLon),
			current.radius,
			tilt,
			bearing,
			current.theta
		)

		// Only write (and request a frame) when the pose actually moves; a
		// stationary user with a steady heading costs nothing
		const moved = Math.abs(target.theta - current.theta) > 1e-9
			|| Math.abs(target.phi - current.phi) > 1e-9
			|| Math.abs(shortestTurn(current.bearing, target.bearing)) > 1e-6
			|| Math.abs(target.tilt - current.tilt) > 1e-6
		if (!moved) return

		this.cameraState.setParamsImmediate(target)
		this.sincePublish += dt
		if (this.sincePublish >= cPublishIntervalSeconds) {
			this.sincePublish = 0
			this.cameraState.saveCameraState()
		}
	}

	/** Compass heading as a camera bearing (radians), only in heading mode */
	private headingFor(headingDeg: number | null): number | null {
		if (this.mode !== "heading" || headingDeg === null) return null
		return headingDeg * trig.rad
	}

	/** Terrain-seated world position of a fix */
	private groundPoint(lat: number, lon: number) {
		const geo = new geographic(lon * trig.rad, lat * trig.rad, 0)
		return new geographic(geo.longitude, geo.latitude, this.context.displayElevationKm(geo)).cartesian
	}

	/**
	 * Entry flight destination. First visit (or from high above): the
	 * local altitude, widened for a coarse fix. Re-center: the current
	 * altitude, so a user who zoomed out to look around and taps the
	 * button again gets the same scale back under them
	 */
	private flightTarget(fix: UserLocationFix, headingRad: number | null): CameraParams {
		const current = this.cameraState.getCurrentParams()
		const fresh = !this.hasFlown || current.radius - wgs.a > cRecenterMaxAltitudeKm
		const altitudeKm = fresh
			? Math.max(cFollowAltitudeKm, fix.accuracyM / 1000 * cAccuracyAltitudeFactor)
			: current.radius - wgs.a
		const radius = this.cameraState.elevationRange.clamp(wgs.a + altitudeKm)
		const tilt = Math.min(current.tilt, this.cameraState.maxTilt(radius) - 0.001)
		const bearing = headingRad !== null ? this.cameraState.clipBearing(headingRad) : current.bearing
		return EarthCameraState.paramsLookingAt(this.groundPoint(fix.lat, fix.lon), radius, tilt, bearing, current.theta)
	}
}
